import React, { useState } from 'react'
import { Eye, EyeOff, Key, AlertCircle } from 'lucide-react'
import Modal from '../Modal/Modal'
import Button from '../Button/Button'
import './ResetPasswordModal.css'

/**
 * Reset Password Modal Component
 *
 * A modal dialog for manually setting a user's password.
 * Used by Super Admin and Company Admin to reset team member passwords.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Modal visibility state
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSubmit - Submit handler (receives password)
 * @param {Object} props.user - User object (for displaying name)
 * @param {boolean} props.loading - Loading state
 */
const ResetPasswordModal = ({
	isOpen,
	onClose,
	onSubmit,
	user,
	loading = false,
}) => {
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [errors, setErrors] = useState({})

	const userName = user ? `${user.first_name} ${user.last_name}` : 'User'

	/**
	 * Validate password requirements
	 */
	const validatePassword = (pwd) => {
		const errors = []
		if (pwd.length < 8) {
			errors.push('Password must be at least 8 characters')
		}
		if (!/[A-Z]/.test(pwd)) {
			errors.push('Password must contain at least one uppercase letter')
		}
		if (!/[a-z]/.test(pwd)) {
			errors.push('Password must contain at least one lowercase letter')
		}
		if (!/[0-9]/.test(pwd)) {
			errors.push('Password must contain at least one number')
		}
		return errors
	}

	/**
	 * Handle form submission
	 */
	const handleSubmit = (e) => {
		e.preventDefault()

		const newErrors = {}

		// Validate password
		const passwordErrors = validatePassword(password)
		if (passwordErrors.length > 0) {
			newErrors.password = passwordErrors[0]
		}

		// Validate confirm password
		if (!confirmPassword) {
			newErrors.confirmPassword = 'Please confirm the password'
		} else if (password !== confirmPassword) {
			newErrors.confirmPassword = 'Passwords do not match'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		// Clear errors and submit
		setErrors({})
		onSubmit(password)
	}

	/**
	 * Handle modal close
	 */
	const handleClose = () => {
		// Reset form state
		setPassword('')
		setConfirmPassword('')
		setShowPassword(false)
		setShowConfirmPassword(false)
		setErrors({})
		onClose()
	}

	/**
	 * Check password strength
	 */
	const getPasswordStrength = () => {
		if (!password) return null

		let strength = 0
		if (password.length >= 8) strength++
		if (password.length >= 12) strength++
		if (/[A-Z]/.test(password)) strength++
		if (/[a-z]/.test(password)) strength++
		if (/[0-9]/.test(password)) strength++
		if (/[!@#$%^&*]/.test(password)) strength++

		if (strength <= 2) return { level: 'weak', label: 'Weak', color: 'var(--color-error)' }
		if (strength <= 4) return { level: 'medium', label: 'Medium', color: 'var(--color-warning)' }
		return { level: 'strong', label: 'Strong', color: 'var(--color-success)' }
	}

	const passwordStrength = getPasswordStrength()

	const footer = (
		<div className="reset-password-modal__actions">
			<Button
				variant="secondary"
				onClick={handleClose}
				disabled={loading}
			>
				Cancel
			</Button>
			<Button
				variant="primary"
				onClick={handleSubmit}
				disabled={loading || !password || !confirmPassword}
				loading={loading}
			>
				<Key size={16} />
				Set Password
			</Button>
		</div>
	)

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Reset Password"
			size="small"
			footer={footer}
			closeOnBackdrop={false}
		>
			<form onSubmit={handleSubmit} className="reset-password-modal">
				<div className="reset-password-modal__info">
					<AlertCircle size={16} />
					<span>
						Set a new password for <strong>{userName}</strong>
					</span>
				</div>

				{/* New Password Field */}
				<div className="form-group">
					<label htmlFor="new-password" className="form-label">
						New Password <span className="required">*</span>
					</label>
					<div className="password-input-wrapper">
						<input
							type={showPassword ? 'text' : 'password'}
							id="new-password"
							className={`form-input ${errors.password ? 'form-input--error' : ''}`}
							value={password}
							onChange={(e) => {
								setPassword(e.target.value)
								if (errors.password) {
									setErrors({ ...errors, password: null })
								}
							}}
							placeholder="Enter new password"
							autoComplete="new-password"
							disabled={loading}
						/>
						<button
							type="button"
							className="password-toggle"
							onClick={() => setShowPassword(!showPassword)}
							tabIndex={-1}
						>
							{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
						</button>
					</div>
					{errors.password && (
						<span className="form-error">{errors.password}</span>
					)}
					{passwordStrength && (
						<div className="password-strength">
							<div className="password-strength__bar">
								<div
									className={`password-strength__fill password-strength__fill--${passwordStrength.level}`}
									style={{ backgroundColor: passwordStrength.color }}
								/>
							</div>
							<span
								className="password-strength__label"
								style={{ color: passwordStrength.color }}
							>
								{passwordStrength.label}
							</span>
						</div>
					)}
				</div>

				{/* Confirm Password Field */}
				<div className="form-group">
					<label htmlFor="confirm-password" className="form-label">
						Confirm Password <span className="required">*</span>
					</label>
					<div className="password-input-wrapper">
						<input
							type={showConfirmPassword ? 'text' : 'password'}
							id="confirm-password"
							className={`form-input ${errors.confirmPassword ? 'form-input--error' : ''}`}
							value={confirmPassword}
							onChange={(e) => {
								setConfirmPassword(e.target.value)
								if (errors.confirmPassword) {
									setErrors({ ...errors, confirmPassword: null })
								}
							}}
							placeholder="Confirm new password"
							autoComplete="new-password"
							disabled={loading}
						/>
						<button
							type="button"
							className="password-toggle"
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
							tabIndex={-1}
						>
							{showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
						</button>
					</div>
					{errors.confirmPassword && (
						<span className="form-error">{errors.confirmPassword}</span>
					)}
				</div>

				{/* Password Requirements */}
				<div className="password-requirements">
					<p className="password-requirements__title">Password must contain:</p>
					<ul className="password-requirements__list">
						<li className={password.length >= 8 ? 'met' : ''}>
							At least 8 characters
						</li>
						<li className={/[A-Z]/.test(password) ? 'met' : ''}>
							One uppercase letter
						</li>
						<li className={/[a-z]/.test(password) ? 'met' : ''}>
							One lowercase letter
						</li>
						<li className={/[0-9]/.test(password) ? 'met' : ''}>
							One number
						</li>
					</ul>
				</div>
			</form>
		</Modal>
	)
}

export default ResetPasswordModal
